/** Production Directory
 *
 *  Filterable, proximity-ranked listing of ALL discoverable users on
 *  Call Sheet — Talent, Junior Artists, and Technical Crew. Powers the
 *  casting-director / production-office day-to-day search flow.
 *
 *  Filters:  role · city · taxonomy · free-text
 *  Proximity: opt-in "Near me" toggle pings the browser Geolocation API
 *             then sets `?near=<city>` after reverse-lookup (city echoed
 *             back from the backend on the /location PATCH response).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AffiliationBadges from "@/components/AffiliationBadges";
import { toast } from "sonner";
import {
  Users, MapPin, Search, X, Compass, Filter, ChevronRight, ShieldCheck, BadgeCheck, Sparkles,
} from "lucide-react";

const PAGE_SIZE = 30;
const TAXONOMY_LABELS = { featured_cast: "Featured Cast", junior_artist: "Junior Artist" };

export default function Directory() {
  const [searchParams] = useSearchParams();
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets]   = useState({ cities: [], roles: [], total_listed: 0, taxonomy_keys: [] });

  const [q, setQ]         = useState("");
  const [city, setCity]   = useState("");
  const [role, setRole]   = useState("");
  const [taxonomy, setTaxonomy] = useState("");
  const [near, setNear]   = useState("");
  const [nearMe, setNearMe] = useState(false);
  const [skip, setSkip]   = useState(0);
  // Variety-artist filters — driven by URL, no chips (yet).
  const [talentSubtype, setTalentSubtype] = useState(searchParams.get("talent_subtype") || "");
  const [varietySkill, setVarietySkill]   = useState(searchParams.get("variety_skill") || "");
  const [varietyCatalog, setVarietyCatalog] = useState([]);
  useEffect(() => {
    api.get("/meta/variety-skills")
      .then(({ data }) => setVarietyCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (q.trim())         params.q        = q.trim();
      if (city)             params.city     = city;
      if (role)             params.role     = role;
      if (taxonomy)         params.taxonomy = taxonomy;
      if (near)             params.near     = near;
      if (talentSubtype)    params.talent_subtype = talentSubtype;
      if (varietySkill)     params.variety_skill  = varietySkill;
      const { data } = await api.get("/directory", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Directory load failed");
    } finally {
      setLoading(false);
    }
  };

  const loadFacets = async () => {
    try {
      const { data } = await api.get("/directory/facets");
      setFacets(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { reload(); }, [q, city, role, taxonomy, near, skip, talentSubtype, varietySkill]);

  const clearFilters = () => {
    setQ(""); setCity(""); setRole(""); setTaxonomy(""); setNear("");
    setNearMe(false); setSkip(0);
    setTalentSubtype(""); setVarietySkill("");
  };

  const askForLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setNearMe(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        // Best-effort reverse lookup — for v1 we simply send lat/lng
        // and let the user's own profile city echo back as the proxy.
        const { data } = await api.patch("/profile/me/location", {
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
        });
        const detectedCity = data?.current_geo_location?.city || "";
        if (detectedCity) {
          setNear(detectedCity);
          toast.success(`Near ${detectedCity}: showing proximity-first results`);
        } else {
          // No reverse lookup yet — ask user to pick from facets.
          toast.info("Location captured. Pick your city from the chips below to prioritise nearby results.");
        }
      } catch (e) {
        toast.error("Location update failed");
        setNearMe(false);
      }
    }, () => {
      toast.error("Location permission denied");
      setNearMe(false);
    }, { timeout: 8000, maximumAge: 60_000 });
  };

  const hasAnyFilter = q || city || role || taxonomy || near || talentSubtype || varietySkill;
  const from = skip + 1;
  const to   = Math.min(skip + items.length, total);

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24" data-testid="directory-page">
      <div className="mx-auto max-w-7xl px-6 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#F5B82E] mb-1">
              <Users size={12} className="inline mr-1" /> Production Directory
            </div>
            <h1 className="font-display text-4xl">Talent · Cast · Crew</h1>
            <p className="text-white/50 text-sm mt-1">
              Filterable listing of every registered professional. Proximity-first when Near-Me is on.
            </p>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl text-[#F5B82E]" data-testid="directory-total">
              {facets.total_listed.toLocaleString()}
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/40">Listed professionals</div>
          </div>
        </div>

        {/* Variety-artist banner when a subtype filter is active */}
        {talentSubtype === "variety_performer" && (
          <div className="rounded-xl border border-orange-500/40 bg-orange-500/10 p-4 mb-6"
               data-testid="directory-variety-banner">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-orange-300 flex items-center gap-1">
                  <Sparkles size={11}/> Variety Artists
                </div>
                <p className="text-sm text-white/80 mt-1.5 max-w-2xl leading-relaxed">
                  Book comics, magicians, dancers, jugglers and solo musicians for corporates, weddings and stage shows.
                </p>
              </div>
              <button onClick={() => { setTalentSubtype(""); setVarietySkill(""); setSkip(0); }}
                      className="text-[10px] tracking-[0.2em] uppercase text-white/60 hover:text-white underline underline-offset-4"
                      data-testid="directory-variety-clear">
                Clear
              </button>
            </div>
            {varietyCatalog.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5" data-testid="directory-variety-skill-chips">
                {varietyCatalog.map((s) => {
                  const on = varietySkill === s.key;
                  return (
                    <button key={s.key}
                      onClick={() => { setSkip(0); setVarietySkill(on ? "" : s.key); }}
                      className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase transition ${
                        on ? "bg-orange-500 text-black" : "bg-white/8 text-white/70 hover:bg-white/15"
                      }`}
                      data-testid={`directory-variety-skill-${s.key}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Filter bar */}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[280px]">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  placeholder="Search by name, role or bio…"
                  value={q}
                  onChange={(e) => { setSkip(0); setQ(e.target.value); }}
                  className="pl-9 bg-black/40 border-white/15 text-white h-10"
                  data-testid="directory-search"
                />
              </div>
            </div>
            <Button
              onClick={askForLocation}
              variant={nearMe ? "default" : "outline"}
              className={nearMe ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "border-white/20"}
              data-testid="directory-near-me"
            >
              <Compass size={14} className="mr-1.5" />
              {nearMe && near ? `Near ${near}` : "Near me"}
            </Button>
            {hasAnyFilter && (
              <Button variant="ghost" onClick={clearFilters} data-testid="directory-clear-filters"
                      className="text-white/60 hover:text-white">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Taxonomy chips */}
          <div className="flex flex-wrap gap-2 items-center" data-testid="directory-taxonomy-chips">
            <span className="text-[10px] uppercase tracking-[0.16em] text-white/40 mr-2">
              <Filter size={11} className="inline mr-1" /> Taxonomy
            </span>
            {facets.taxonomy_keys.map((tk) => (
              <button
                key={tk}
                onClick={() => { setSkip(0); setTaxonomy(taxonomy === tk ? "" : tk); }}
                data-testid={`directory-tax-${tk}`}
                className={`px-3 py-1 rounded-full border text-[11px] tracking-[0.1em] uppercase transition ${
                  taxonomy === tk
                    ? "bg-[#F5B82E]/20 border-[#F5B82E]/60 text-[#F5B82E]"
                    : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {TAXONOMY_LABELS[tk] || tk}
              </button>
            ))}
          </div>

          {/* City chips */}
          {facets.cities.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center" data-testid="directory-city-chips">
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/40 mr-2">
                <MapPin size={11} className="inline mr-1" /> City
              </span>
              {facets.cities.slice(0, 10).map((c) => (
                <button
                  key={c.key}
                  onClick={() => { setSkip(0); setCity(city === c.key ? "" : c.key); }}
                  data-testid={`directory-city-${c.key}`}
                  className={`px-3 py-1 rounded-full border text-[11px] transition ${
                    city === c.key
                      ? "bg-white/10 border-white/40 text-white"
                      : "border-white/10 text-white/50 hover:text-white/80 hover:border-white/30"
                  }`}
                >
                  {c.key} <span className="opacity-40 text-[10px]">· {c.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Role chips */}
          {facets.roles.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center" data-testid="directory-role-chips">
              <span className="text-[10px] uppercase tracking-[0.16em] text-white/40 mr-2">Role</span>
              {facets.roles.slice(0, 12).map((r) => (
                <button
                  key={r.key}
                  onClick={() => { setSkip(0); setRole(role === r.key ? "" : r.key); }}
                  data-testid={`directory-role-${r.key}`}
                  className={`px-3 py-1 rounded-full border text-[11px] transition ${
                    role === r.key
                      ? "bg-white/10 border-white/40 text-white"
                      : "border-white/10 text-white/50 hover:text-white/80 hover:border-white/30"
                  }`}
                >
                  {r.key} <span className="opacity-40 text-[10px]">· {r.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Result meta */}
        <div className="flex items-center justify-between mb-4 text-xs text-white/50" data-testid="directory-result-meta">
          {loading ? "Loading…" : (
            <>Showing <span className="text-white">{from}-{to}</span> of <span className="text-white">{total.toLocaleString()}</span>{near && ` · proximity: ${near}`}</>
          )}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && items.length === 0 ? (
            <div className="col-span-full text-white/40 text-center py-16" data-testid="directory-loading">
              Loading professionals…
            </div>
          ) : items.length === 0 ? (
            <div className="col-span-full text-white/40 text-center py-16" data-testid="directory-empty">
              No matches. Try clearing filters.
            </div>
          ) : items.map((u) => (
            <DirectoryCard key={u.user_id} u={u} />
          ))}
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="directory-pagination">
            <Button
              variant="ghost" disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              className="text-white/70"
              data-testid="directory-prev"
            >
              ← Previous
            </Button>
            <span className="text-xs text-white/50">
              Page {Math.floor(skip / PAGE_SIZE) + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </span>
            <Button
              variant="ghost"
              disabled={skip + PAGE_SIZE >= total}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              className="text-white/70"
              data-testid="directory-next"
            >
              Next →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function DirectoryCard({ u }) {
  const proximityHit = u.current_geo_location?.city && u.primary_association_city &&
    u.current_geo_location.city.toLowerCase() === u.primary_association_city.toLowerCase();
  return (
    <Link to={`/discover/${u.user_id}`}
          data-testid={`directory-card-${u.user_id}`}
          className="group block rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#F5B82E]/40 hover:bg-white/[0.04] transition p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
            {u.role_label || u.primary_role || "—"}
          </div>
          <div className="font-display text-lg mt-0.5">{u.full_name}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {u.is_member ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[9px] tracking-[0.18em] uppercase font-semibold"
                  data-testid={`directory-member-chip-${u.user_id}`}
                  title={u.association_name ? `Member — ${u.association_name}` : "Union member"}>
              <BadgeCheck size={9}/> Member
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/[0.06] text-amber-300/85 text-[9px] tracking-[0.18em] uppercase font-semibold"
                  data-testid={`directory-non-member-chip-${u.user_id}`}
                  title="Independent · Not affiliated to any body">
              Non-Member
            </span>
          )}
          {u.verified && (
            <div className="rounded-full bg-emerald-500/10 border border-emerald-500/40 p-1.5"
                 title="Verified">
              <ShieldCheck size={12} className="text-emerald-400" />
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-white/60 mb-3">
        <MapPin size={11} />
        <span>{u.primary_association_city || u.home_city || "—"}</span>
        {proximityHit && (
          <span className="ml-auto text-[9px] tracking-[0.16em] uppercase text-emerald-400/80">
            Currently here
          </span>
        )}
      </div>
      {u.association_name && (
        <div className="text-xs text-white/40 mb-2">
          <span className="font-semibold text-white/70">{u.association_name}</span>
          {u.membership_number && <span className="font-mono opacity-60 ml-1.5">#{u.membership_number}</span>}
        </div>
      )}
      {u.section_affiliations?.length > 0 && (
        <AffiliationBadges items={u.section_affiliations} compact
                            testidPrefix={`directory-badges-${u.user_id}`} />
      )}
      <VouchChips vouches={u.vouches} testidPrefix={`directory-vouch-${u.user_id}`} />
      <div className="mt-4 flex items-center justify-end text-[10px] uppercase tracking-[0.2em] text-white/40 group-hover:text-[#F5B82E] transition">
        Open profile <ChevronRight size={12} className="ml-0.5" />
      </div>
    </Link>
  );
}

/** Verified-by-<Trade Org> chip strip. Renders one chip per accepted
 *  trade-org vouch. Each chip is a deep-link to the vouching org's
 *  public page (agency handle when available, directory fallback
 *  otherwise). Chip clicks stop propagation so they don't also open
 *  the parent card.
 */
function VouchChips({ vouches, testidPrefix }) {
  if (!Array.isArray(vouches) || vouches.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testidPrefix}>
      {vouches.map((v) => {
        const href = v.org_handle
          ? `/agency/${v.org_handle}`
          : `/directory/orgs?type=trade_org&q=${encodeURIComponent(v.org_name || "")}`;
        return (
          <Link
            key={v.org_id}
            to={href}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testidPrefix}-${v.org_id}`}
            title={`Verified by ${v.org_name}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10px] tracking-[0.06em] hover:bg-emerald-500/20 hover:border-emerald-400 transition"
          >
            <BadgeCheck size={10} />
            <span className="truncate max-w-[140px]">Verified by {v.org_name}</span>
          </Link>
        );
      })}
    </div>
  );
}
